/**
 * Disk Storage Service for persisting data
 *
 * This service provides functions to save and load data from disk storage via API calls.
 * It provides a consistent interface for saving and loading data.
 */
import dataTypeService from '../services/dataTypeService';
import versionService from '../services/versionService';
import authService from '../services/authService';
import cacheService from '../services/cacheService';
import fileLogger from './logger';
import { createErrorLogger, ErrorType } from './errorHandler';

const logger = createErrorLogger('StorageService');

// Memory cache to improve performance
const memoryCache = {};

/**
 * Save data to disk storage
 * @param {string} key - The storage key
 * @param {any} data - The data to save
 * @returns {Promise<boolean>} - Success flag
 */
export const saveToDiskStorage = async (key, data, userId) => {
  try {
    logger.info('saveToDiskStorage', `START key: ${key}`);
    // Update memory cache
    memoryCache[key] = data;
    logger.info('saveToDiskStorage', `Updated memory cache for key: ${key}`);

    // Save to disk storage via API - use proper user context
    logger.info('saveToDiskStorage', `Saving to database service for key: ${key}`);

    // Get userId from parameter - caller must provide valid user ID
    if (!userId) {
      throw new Error('userId is required for disk storage operations');
    }

    // Convert boolean values to strings in JSON when saving to prevent 'true' folder creation
    const processedData = typeof data === 'boolean' ? JSON.stringify(data) : data;

    // Direct call to dataTypeService with proper type mapping
    const { component, filename } = dataTypeService.getTypeMapping(key);
    await versionService.saveVersionedData(userId, component, filename, processedData);
    logger.info('saveToDiskStorage', `Successfully saved data for key: ${key}`);
    fileLogger.debug('DISK_STORAGE', 'Saved data to disk storage', { key });
    return true;
  } catch (error) {
    logger.error('saveToDiskStorage', `ERROR - key: ${key}`, error, ErrorType.DATA_SAVE);
    fileLogger.error('DISK_STORAGE', 'Error saving data to disk storage', {
      key,
      error: error.message
    });
    return false;
  }
};

/**
 * Get data from disk storage
 * @param {string} key - The storage key
 * @param {any} defaultValue - Default value if not found
 * @returns {Promise<any>} - The data
 */
export const getFromDiskStorage = async (key, userId, defaultValue = {}) => {
  try {
    logger.info('getFromDiskStorage', `START key: ${key}`);

    // Check memory cache first for performance
    if (memoryCache[key] !== undefined) {
      logger.info('getFromDiskStorage', `Using memory cache for key: ${key}`);
      return memoryCache[key];
    }

    // Load from disk storage via API - use proper user context
    logger.info('getFromDiskStorage', `Fetching from database service for key: ${key}`);

    // Get userId from parameter - caller must provide valid user ID
    if (!userId) {
      throw new Error('userId is required for disk storage operations');
    }

    // Direct call to versionService with proper type mapping
    const { component, filename } = dataTypeService.getTypeMapping(key);
    const response = await versionService.loadVersionedData(userId, component, filename);
    logger.info('getFromDiskStorage', `Database response for key ${key}`, { response });

    // API returns {result: data} format
    if (response && response.result !== undefined) {
      // Cache the result
      memoryCache[key] = response.result;
      logger.info('getFromDiskStorage', `Cached result for key ${key}`);
      fileLogger.debug('DISK_STORAGE', 'Loaded and cached data', { key, cached: true });
      return response.result;
    }

    logger.info('getFromDiskStorage', `No data found for key ${key}, returning default`);
    fileLogger.info('DISK_STORAGE', 'No data found, returning default', { key });
    return defaultValue;
  } catch (error) {
    logger.error('getFromDiskStorage', `ERROR - key: ${key}`, error, ErrorType.DATA_LOAD);
    fileLogger.error('DISK_STORAGE', 'Error getting data from disk storage', {
      key,
      error: error.message
    });
    return defaultValue;
  }
};

/**
 * Get data specifically for a user
 * @param {string} key - The storage key
 * @param {string} userId - The user ID
 * @param {any} defaultValue - Default value if not found
 * @returns {Promise<any>} - The user's data
 */
export const getUserData = async (key, userId, defaultValue = {}) => {
  try {
    logger.info('getUserData', `START key: ${key}, userId: ${userId}`);

    if (!userId) {
      throw new Error(`getUserData requires a valid userId for key: ${key}`);
    }

    // Load user-specific data from database service
    logger.info('getUserData', `Loading from database service for key: ${key}, userId: ${userId}`);

    // Direct call to versionService with proper type mapping
    const { component, filename } = dataTypeService.getTypeMapping(key);
    const response = await versionService.loadVersionedData(userId, component, filename);
    logger.info('getUserData', `Database response for key ${key}, userId ${userId}`, { response });

    // API returns data directly or wrapped in result
    if (response && response.result !== undefined) {
      logger.info('getUserData', `Found data for key ${key}, userId ${userId}`);
      fileLogger.debug('DISK_STORAGE', 'Loaded user data', {
        key,
        userId,
        dataFound: true
      });
      return response.result;
    } else if (response) {
      // Direct response format
      logger.info('getUserData', `Found data for key ${key}, userId ${userId}`);
      fileLogger.debug('DISK_STORAGE', 'Loaded user data', {
        key,
        userId,
        dataFound: true
      });
      return response;
    }

    logger.info('getUserData', `No data found for key ${key}, userId ${userId}`);
    fileLogger.info('DISK_STORAGE', 'No user data found, returning default', { key, userId });
    return defaultValue;
  } catch (error) {
    logger.error('getUserData', `ERROR - key: ${key}, userId: ${userId}`, error, ErrorType.DATA_LOAD);
    fileLogger.error('DISK_STORAGE', 'Error getting user data', {
      key,
      userId,
      error: error.message
    });
    throw error;
  }
};

/**
 * Update user-specific data
 * @param {string} key - The storage key
 * @param {string} userId - The user ID
 * @param {any} data - The data to save
 * @returns {Promise<boolean>} - Success flag
 */
export const updateUserData = async (key, userId, data) => {
  try {
    logger.info('updateUserData', `START key: ${key}, userId: ${userId}`);

    if (!userId) {
      logger.error('updateUserData', `Missing userId for key: ${key}`, null, ErrorType.VALIDATION);
      fileLogger.error('DISK_STORAGE', 'Cannot update user data without userId', { key });
      throw new Error(`Missing userId for key: ${key}`);
    }

    // Save user-specific data via database service
    logger.info('updateUserData', `Saving to database service for key: ${key}, userId: ${userId}`);
    logger.info('updateUserData', `Data sample: ${JSON.stringify(data).substring(0, 200)}...`);

    // Direct call to versionService with proper type mapping
    const { component, filename } = dataTypeService.getTypeMapping(key);
    await versionService.saveVersionedData(userId, component, filename, data);
    logger.info('updateUserData', `Successfully saved data for key: ${key}, userId: ${userId}`);

    fileLogger.debug('DISK_STORAGE', 'Updated user data', {
      key,
      userId,
      dataSize: JSON.stringify(data).length
    });

    return true;
  } catch (error) {
    logger.error('updateUserData', `ERROR - key: ${key}, userId: ${userId}`, error, ErrorType.DATA_SAVE);
    fileLogger.error('DISK_STORAGE', 'Error updating user data', {
      key,
      userId,
      error: error.message
    });
    throw error;
  }
};

/**
 * Clear data from disk storage
 * @param {string} key - The key to clear
 * @returns {Promise<boolean>} - Success flag
 */
export const clearFromDiskStorage = async (key) => {
  try {
    logger.info('clearFromDiskStorage', `START key: ${key}`);
    // Clear from memory cache
    delete memoryCache[key];
    logger.info('clearFromDiskStorage', `Removed from memory cache for key: ${key}`);

    // Clear from disk storage via API
    logger.info('clearFromDiskStorage', `Clearing from database service for key: ${key}`);

    cacheService.clear(key);
    logger.info('clearFromDiskStorage', `Successfully cleared data for key: ${key}`);
    fileLogger.debug('DISK_STORAGE', 'Cleared data from disk storage', { key });
    return true;
  } catch (error) {
    logger.error('clearFromDiskStorage', `ERROR - key: ${key}`, error, ErrorType.DATA_SAVE);
    fileLogger.error('DISK_STORAGE', 'Error clearing data from disk storage', {
      key,
      error: error.message
    });
    return false;
  }
};

/**
 * Clear the memory cache
 * @returns {void}
 */
export const clearMemoryCache = () => {
  logger.info('clearMemoryCache', 'START');
  Object.keys(memoryCache).forEach(key => {
    delete memoryCache[key];
  });
  logger.info('clearMemoryCache', 'Memory cache cleared');
  fileLogger.debug('DISK_STORAGE', 'Memory cache cleared');
};

/**
 * Get user versions - standardized wrapper for version history
 * @param {string} type - The data type (cv, job, company)
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} - Array of version objects
 */
export const getUserVersions = async (type, userId) => {
  try {
    logger.info('getUserVersions', `START type: ${type}, userId: ${userId}`);

    if (!userId) {
      throw new Error(`getUserVersions requires a valid userId for type: ${type}`);
    }

    const versions = await versionService.getUserVersions(type, userId);
    logger.info('getUserVersions', `Found ${versions?.length || 0} versions for type: ${type}, userId: ${userId}`);

    fileLogger.debug('DISK_STORAGE', 'Loaded user versions', {
      type,
      userId,
      versionCount: versions?.length || 0
    });

    return versions || [];
  } catch (error) {
    logger.error('getUserVersions', `ERROR - type: ${type}, userId: ${userId}`, error, ErrorType.DATA_LOAD);
    fileLogger.error('DISK_STORAGE', 'Error getting user versions', {
      type,
      userId,
      error: error.message
    });
    return [];
  }
};

/**
 * Load versioned data - standardized wrapper for loading specific version
 * @param {string} userId - The user ID
 * @param {string} component - The component name
 * @param {string} filename - The filename
 * @returns {Promise<any>} - The versioned data
 */
export const loadVersionedData = async (userId, component, filename) => {
  try {
    logger.info('loadVersionedData', `START userId: ${userId}, component: ${component}, filename: ${filename}`);

    if (!userId) {
      throw new Error(`loadVersionedData requires a valid userId for component: ${component}`);
    }

    const data = await versionService.loadVersionedData(userId, component, filename);
    logger.info('loadVersionedData', `Loaded data for userId: ${userId}, component: ${component}`);

    fileLogger.debug('DISK_STORAGE', 'Loaded versioned data', {
      userId,
      component,
      filename,
      dataFound: !!data
    });

    return data;
  } catch (error) {
    logger.error('loadVersionedData', `ERROR - userId: ${userId}, component: ${component}`, error, ErrorType.DATA_LOAD);
    fileLogger.error('DISK_STORAGE', 'Error loading versioned data', {
      userId,
      component,
      filename,
      error: error.message
    });
    return null;
  }
};

/**
 * Save versioned data - standardized wrapper for saving with version history
 * @param {string} userId - The user ID
 * @param {string} component - The component name
 * @param {string} filename - The filename
 * @param {any} data - The data to save
 * @returns {Promise<boolean>} - Success flag
 */
export const saveVersionedData = async (userId, component, filename, data) => {
  try {
    logger.info('saveVersionedData', `START userId: ${userId}, component: ${component}, filename: ${filename}`);

    if (!userId) {
      logger.error('saveVersionedData', 'Missing userId', null, ErrorType.VALIDATION);
      fileLogger.error('DISK_STORAGE', 'Cannot save versioned data without userId');
      throw new Error('Missing userId');
    }

    const result = await versionService.saveVersionedData(userId, component, filename, data);
    logger.info('saveVersionedData', 'Save result', { result });

    fileLogger.debug('DISK_STORAGE', 'Saved versioned data', {
      userId,
      component,
      filename,
      dataSize: JSON.stringify(data).length,
      success: result?.status === 'success'
    });

    return result?.status === 'success';
  } catch (error) {
    logger.error('saveVersionedData', `ERROR - userId: ${userId}, component: ${component}`, error, ErrorType.DATA_SAVE);
    fileLogger.error('DISK_STORAGE', 'Error saving versioned data', {
      userId,
      component,
      filename,
      error: error.message
    });
    throw error;
  }
};

/**
 * Save competencies data
 * @param {string} userId - The user ID
 * @param {any} competencies - The competencies data
 * @returns {Promise<any>} - Save result
 */
export const saveCompetencies = async (userId, competencies) => {
  try {
    logger.info('saveCompetencies', `START userId: ${userId}`);

    if (!userId) {
      logger.error('saveCompetencies', 'Missing userId', null, ErrorType.VALIDATION);
      fileLogger.error('DISK_STORAGE', 'Cannot save competencies without userId');
      return { status: 'error', message: 'Missing userId' };
    }

    const result = await dataTypeService.saveCompetencies(userId, competencies);
    logger.info('saveCompetencies', 'Save result', { result });

    fileLogger.debug('DISK_STORAGE', 'Saved competencies', {
      userId,
      dataSize: JSON.stringify(competencies).length
    });

    return result;
  } catch (error) {
    logger.error('saveCompetencies', `ERROR - userId: ${userId}`, error, ErrorType.DATA_SAVE);
    fileLogger.error('DISK_STORAGE', 'Error saving competencies', {
      userId,
      error: error.message
    });
    return { status: 'error', message: error.message };
  }
};

/**
 * Load competencies data
 * @param {string} userId - The user ID
 * @param {string} version - Optional version
 * @returns {Promise<any>} - The competencies data
 */
export const loadCompetencies = async (userId, version = null) => {
  try {
    logger.info('loadCompetencies', `START userId: ${userId}, version: ${version}`);

    if (!userId) {
      logger.warn('loadCompetencies', 'Missing userId');
      fileLogger.warn('DISK_STORAGE', 'Cannot load competencies without userId');
      return null;
    }

    const data = await dataTypeService.loadCompetencies(userId, version);
    logger.info('loadCompetencies', `Loaded data for userId: ${userId}`);

    fileLogger.debug('DISK_STORAGE', 'Loaded competencies', {
      userId,
      version,
      dataFound: !!data
    });

    return data;
  } catch (error) {
    logger.error('loadCompetencies', `ERROR - userId: ${userId}`, error, ErrorType.DATA_LOAD);
    fileLogger.error('DISK_STORAGE', 'Error loading competencies', {
      userId,
      version,
      error: error.message
    });
    return null;
  }
};

/**
 * Save job skills data
 * @param {string} userId - The user ID
 * @param {any} skills - The skills data
 * @returns {Promise<any>} - Save result
 */
export const saveJobSkills = async (userId, skills) => {
  try {
    logger.info('saveJobSkills', `START userId: ${userId}`);

    if (!userId) {
      logger.error('saveJobSkills', 'Missing userId', null, ErrorType.VALIDATION);
      fileLogger.error('DISK_STORAGE', 'Cannot save job skills without userId');
      return { status: 'error', message: 'Missing userId' };
    }

    const result = await dataTypeService.saveJobSkills(userId, skills);
    logger.info('saveJobSkills', 'Save result', { result });

    fileLogger.debug('DISK_STORAGE', 'Saved job skills', {
      userId,
      dataSize: JSON.stringify(skills).length
    });

    return result;
  } catch (error) {
    logger.error('saveJobSkills', `ERROR - userId: ${userId}`, error, ErrorType.DATA_SAVE);
    fileLogger.error('DISK_STORAGE', 'Error saving job skills', {
      userId,
      error: error.message
    });
    return { status: 'error', message: error.message };
  }
};

/**
 * Load job skills data
 * @param {string} userId - The user ID
 * @param {string} version - Optional version
 * @returns {Promise<any>} - The job skills data
 */
export const loadJobSkills = async (userId, version = null) => {
  try {
    logger.info('loadJobSkills', `START userId: ${userId}, version: ${version}`);

    if (!userId) {
      logger.warn('loadJobSkills', 'Missing userId');
      fileLogger.warn('DISK_STORAGE', 'Cannot load job skills without userId');
      return null;
    }

    const data = await dataTypeService.loadJobSkills(userId, version);
    logger.info('loadJobSkills', `Loaded data for userId: ${userId}`);

    fileLogger.debug('DISK_STORAGE', 'Loaded job skills', {
      userId,
      version,
      dataFound: !!data
    });

    return data;
  } catch (error) {
    logger.error('loadJobSkills', `ERROR - userId: ${userId}`, error, ErrorType.DATA_LOAD);
    fileLogger.error('DISK_STORAGE', 'Error loading job skills', {
      userId,
      version,
      error: error.message
    });
    return null;
  }
};

/**
 * Save job skills with version metadata
 * @param {string} userId - The user ID
 * @param {any} skills - The skills data
 * @param {any} versionMetadata - Version metadata
 * @returns {Promise<any>} - Save result
 */
export const saveJobSkillWithVersion = async (userId, skills, versionMetadata) => {
  try {
    logger.info('saveJobSkillWithVersion', `START userId: ${userId}`);

    if (!userId) {
      logger.error('saveJobSkillWithVersion', 'Missing userId', null, ErrorType.VALIDATION);
      fileLogger.error('DISK_STORAGE', 'Cannot save job skills with version without userId');
      return { status: 'error', message: 'Missing userId' };
    }

    const result = await dataTypeService.saveJobSkillWithVersion(userId, skills, versionMetadata);
    logger.info('saveJobSkillWithVersion', 'Save result', { result });

    fileLogger.debug('DISK_STORAGE', 'Saved job skills with version', {
      userId,
      dataSize: JSON.stringify(skills).length,
      hasVersionMetadata: !!versionMetadata
    });

    return result;
  } catch (error) {
    logger.error('saveJobSkillWithVersion', `ERROR - userId: ${userId}`, error, ErrorType.DATA_SAVE);
    fileLogger.error('DISK_STORAGE', 'Error saving job skills with version', {
      userId,
      error: error.message
    });
    return { status: 'error', message: error.message };
  }
};

/**
 * Save competencies with version metadata
 * @param {string} userId - The user ID
 * @param {any} competencies - The competencies data
 * @param {any} versionMetadata - Version metadata
 * @returns {Promise<any>} - Save result
 */
export const saveCompetenciesWithVersion = async (userId, competencies, versionMetadata) => {
  try {
    logger.info('saveCompetenciesWithVersion', `START userId: ${userId}`);

    if (!userId) {
      logger.error('saveCompetenciesWithVersion', 'Missing userId', null, ErrorType.VALIDATION);
      fileLogger.error('DISK_STORAGE', 'Cannot save competencies with version without userId');
      return { status: 'error', message: 'Missing userId' };
    }

    const result = await dataTypeService.saveCompetenciesWithVersion(userId, competencies, versionMetadata);
    logger.info('saveCompetenciesWithVersion', 'Save result', { result });

    fileLogger.debug('DISK_STORAGE', 'Saved competencies with version', {
      userId,
      dataSize: JSON.stringify(competencies).length,
      hasVersionMetadata: !!versionMetadata
    });

    return result;
  } catch (error) {
    logger.error('saveCompetenciesWithVersion', `ERROR - userId: ${userId}`, error, ErrorType.DATA_SAVE);
    fileLogger.error('DISK_STORAGE', 'Error saving competencies with version', {
      userId,
      error: error.message
    });
    return { status: 'error', message: error.message };
  }
};

// Re-export auth helpers for convenience
export const getUsers = authService.listUsers;
export const updateUserRole = authService.updateUserRole;
export const assignUserManager = authService.assignManager;

/**
 * Export both named and default exports for flexibility
 */
const StorageService = {
  getFromDiskStorage,
  saveToDiskStorage,
  clearFromDiskStorage,
  updateUserData,
  getUserData,
  clearMemoryCache,
  getUserVersions,
  loadVersionedData,
  saveVersionedData,
  saveCompetencies,
  loadCompetencies,
  saveJobSkills,
  loadJobSkills,
  saveJobSkillWithVersion,
  saveCompetenciesWithVersion
};

// System-level storage functions that don't require user context
// These are used for application metadata, not user data
export const saveSystemData = async (key, data) => {
  logger.info('saveSystemData', `Saving system data for key: ${key}`);

  try {
    // Use 'system' as a special userId for system-level data
    const SYSTEM_USER_ID = 'system';

    // Update memory cache first
    memoryCache[key] = data;
    logger.info('saveSystemData', `Updated memory cache for key: ${key}`);

    // Save to disk storage via API
    const processedData = typeof data === 'boolean' ? JSON.stringify(data) : data;
    const { component, filename } = dataTypeService.getTypeMapping(key);
    await versionService.saveVersionedData(SYSTEM_USER_ID, component, filename, processedData);
    logger.info('saveSystemData', `Successfully saved system data for key: ${key}`);
    return true;
  } catch (error) {
    logger.error('saveSystemData', `ERROR - key: ${key}`, error, ErrorType.DATA_SAVE);
    throw error;
  }
};

export const getSystemData = async (key, defaultValue = {}) => {
  try {
    logger.info('getSystemData', `Getting system data for key: ${key}`);

    // Check memory cache first
    if (memoryCache[key] !== undefined) {
      logger.info('getSystemData', `Using memory cache for key: ${key}`);
      return memoryCache[key];
    }

    // Use 'system' as a special userId for system-level data
    const SYSTEM_USER_ID = 'system';

    const { component, filename } = dataTypeService.getTypeMapping(key);
    const response = await versionService.loadVersionedData(SYSTEM_USER_ID, component, filename);

    if (response && response.result !== undefined) {
      memoryCache[key] = response.result;
      logger.info('getSystemData', `Cached system result for key ${key}`);
      return response.result;
    }

    logger.info('getSystemData', `Using default value for key ${key}`);
    return defaultValue;
  } catch (error) {
    logger.error('getSystemData', `ERROR - key: ${key}`, error, ErrorType.DATA_LOAD);
    throw error;
  }
};

export default StorageService;
